import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { Property } from "csstype";

import { Box, Tooltip, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

import { enqueueSnackbar } from "notistack";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { playbackActions } from "./store/slice";
import { playbackDebugSelectors } from "./store/selectors.debug";
import { playbackSessionSelectors } from "./store/selectors.session";
import { selectPlaybackConfiguration } from "features/config/store/selectors";

import { usePlaybackStream } from "./hooks/usePlaybackStream";

import { PlayerCard, PlayerCardRef } from "features/videoPlayer/PlayerCard";
import { PlayMode } from "features/videoPlayer/types/PlayMode";

import { DownloadRecordingButton } from "./DownloadRecordingButton";
import { selectInWindowMode } from "features/core/store/selectors";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { HlsPlayer } from "features/videoPlayer/HlsPlayer";
import { PlayerCardStatus } from "features/videoPlayer/PlayerCardStatus";
import { PlayerRef } from "features/videoPlayer/types/PlayerRef";
import { userSelectors } from "features/user/store/selectors";
import { StreamPlayerModeType } from "types/StreamPlayerModeType";

export interface RecordingPlayerProps {
    sessionId: string;
    siteId: EntityId;
    recordingId: EntityId;
    originalStartTime: string;
    deviceId: EntityId;
    deviceName: string;
    playbackStartDateTime: string | undefined;
}

export function RecordingPlayer(props: RecordingPlayerProps) {
    const dispatch = useAppDispatch();

    // debug
    const debugShowBufferingRecordings = useAppSelector(
        playbackDebugSelectors.selectShowBufferingRecordings,
    );
    const debugShowRecordingsOutsidePlaybackAndBufferingWindow = useAppSelector(
        playbackDebugSelectors.selectShowRecordingsOutsidePlaybackAndBufferingWindow,
    );

    // get needed store data
    const audioEnabledByDefault = useAppSelector(
        userSelectors.selectAudioEnabledByDefault,
    );
    const playbackConfiguration = useAppSelector(selectPlaybackConfiguration);

    const selectDeviceRecordingWithPlaybackStateById = useMemo(
        () =>
            playbackSessionSelectors.selectDeviceRecordingWithPlaybackStateByIdFactory(),
        [],
    );
    const recordingWithPlaybackState = useAppSelector((state: RootState) =>
        selectDeviceRecordingWithPlaybackStateById(state, props.recordingId),
    );

    const selectRecordingFailedToLoadById = useMemo(
        () => playbackSessionSelectors.selectRecordingFailedToLoadByIdFactory(),
        [],
    );
    const recordingFailedToLoad = useAppSelector((state: RootState) =>
        selectRecordingFailedToLoadById(state, props.recordingId),
    );

    const playbackPlaying = useAppSelector(
        playbackSessionSelectors.selectPlaybackPlaying,
    );

    const selectCanReuseExistingStreamByRecordingId = useMemo(
        () =>
            playbackSessionSelectors.selectCanReuseExistingStreamByRecordingIdFactory(),
        [],
    );
    const canReuseExistingStreamByRecordingId = useAppSelector(
        (state: RootState) =>
            selectCanReuseExistingStreamByRecordingId(state, props.recordingId),
    );

    // recording stream allbacks and hook initialization
    const onErrorLoadingStream = useCallback(() => {
        // when API call fails
        enqueueSnackbar(
            `Failed to load recording '${props.recordingId}' for device '${props.deviceName}'`,
            {
                variant: "error",
            },
        );

        dispatch(
            playbackActions.setRecordingFailedToLoad({
                recordingId: props.recordingId,
            }),
        );
    }, [dispatch, props.deviceName, props.recordingId]);

    const onTimeoutPlayingStream = useCallback(() => {
        // when API call times out
        enqueueSnackbar(
            `Failed to start recording '${props.recordingId}' for device '${props.deviceName}' in a timely manner`,
            {
                variant: "error",
            },
        );

        dispatch(
            playbackActions.setRecordingFailedToLoad({
                recordingId: props.recordingId,
            }),
        );
    }, [dispatch, props.deviceName, props.recordingId]);

    const {
        initializeStream,
        streamUrl,
        isLoading,
        cancelLoadRecordingTimeout,
    } = usePlaybackStream(
        props.sessionId,
        props.recordingId,
        props.originalStartTime,
        props.deviceId,
        props.playbackStartDateTime,
        playbackConfiguration?.maxRecordingStartupTimeInMs || 0,
        onErrorLoadingStream,
        onTimeoutPlayingStream,
    );

    // conditionally start to load stream
    const shouldBeBufferingOrPlaying =
        recordingWithPlaybackState?.inBufferWindow ||
        recordingWithPlaybackState?.inPlaybackWindow;

    const [
        lastPlaybackStartDateTimeChangedTimestamp,
        setLastPlaybackStartDateTimeChangedTimestamp,
    ] = useState<number | undefined>();
    useEffect(() => {
        // recording must be in buffer/playback window
        if (!shouldBeBufferingOrPlaying) return;

        if (!canReuseExistingStreamByRecordingId.canReuseStream) {
            // if we can't reuse the stream initialize it
            initializeStream();
        } else if (
            lastPlaybackStartDateTimeChangedTimestamp !==
            canReuseExistingStreamByRecordingId.playbackStartDateTimeChangedTimestamp
        ) {
            // we can reuse the stream and we haven't processed it since the last manual change
            setLastPlaybackStartDateTimeChangedTimestamp(
                canReuseExistingStreamByRecordingId.playbackStartDateTimeChangedTimestamp,
            );
            playerRef.current?.videoSetCurrentTime(
                canReuseExistingStreamByRecordingId.currentTime,
            );
        }

        return () => cancelLoadRecordingTimeout();
    }, [
        canReuseExistingStreamByRecordingId.canReuseStream,
        canReuseExistingStreamByRecordingId.currentTime,
        canReuseExistingStreamByRecordingId.playbackStartDateTimeChangedTimestamp,
        cancelLoadRecordingTimeout,
        initializeStream,
        lastPlaybackStartDateTimeChangedTimestamp,
        shouldBeBufferingOrPlaying,
    ]);

    // determine play mode
    const [playMode, setPlayMode] = useState<PlayMode>("stop");
    useEffect(() => {
        if (playbackPlaying && recordingWithPlaybackState?.inPlaybackWindow) {
            setPlayMode("play");
        } else if (
            (!playbackPlaying &&
                recordingWithPlaybackState?.inPlaybackWindow) ||
            recordingWithPlaybackState?.inBufferWindow
        ) {
            setPlayMode("pause");
        } else {
            setPlayMode("stop");
        }
    }, [
        playbackPlaying,
        recordingWithPlaybackState?.inBufferWindow,
        recordingWithPlaybackState?.inPlaybackWindow,
    ]);

    // mark recording as ready
    const onRecordingReadyForPlayback = useCallback(() => {
        if (cancelLoadRecordingTimeout) {
            cancelLoadRecordingTimeout();
        }

        dispatch(
            playbackActions.setRecordingReadyForPlayback({
                recordingId: props.recordingId,
            }),
        );
    }, [cancelLoadRecordingTimeout, dispatch, props.recordingId]);

    // mark recording waiting for data
    const onStreamWaitingForData = useCallback(() => {
        dispatch(
            playbackActions.setRecordingWaiting({
                recordingId: props.recordingId,
            }),
        );
    }, [dispatch, props.recordingId]);

    // cache the player card
    const [show, setShow] = useState<boolean>();

    const playerCardRef = useRef<PlayerCardRef>(null);
    const playerRef = useRef<PlayerRef>(null);
    const inWindowMode = useAppSelector(selectInWindowMode);

    const cachedHlsPlayerCard = useMemo(() => {
        // don't render player card when it is outside buffer/playback window UNLESS debug toggle is set to TRUE
        if (
            !recordingWithPlaybackState ||
            (!shouldBeBufferingOrPlaying &&
                !debugShowRecordingsOutsidePlaybackAndBufferingWindow)
        )
            return;

        const visible =
            (recordingWithPlaybackState &&
                recordingWithPlaybackState.inPlaybackWindow) ||
            (debugShowBufferingRecordings &&
                recordingWithPlaybackState.inBufferWindow) ||
            (debugShowRecordingsOutsidePlaybackAndBufferingWindow &&
                !recordingWithPlaybackState.inBufferWindow &&
                !recordingWithPlaybackState.inPlaybackWindow);

        setShow(visible);

        const tooltipText = `${DateTime.fromISO(recordingWithPlaybackState.startTime).toLocaleString(DateTime.DATETIME_MED)} - ${DateTime.fromISO(recordingWithPlaybackState.stopTime).toLocaleString(DateTime.DATETIME_MED)} - original: ${DateTime.fromISO(recordingWithPlaybackState.originalStartTime).toLocaleString(DateTime.DATETIME_MED)}`;

        let videoMouseOverCursor: Property.Cursor | undefined;
        if (document.fullscreenEnabled) {
            videoMouseOverCursor = inWindowMode ? "zoom-in" : "zoom-out";
        }

        return (
            <PlayerCard
                ref={playerCardRef}
                sx={{
                    ...(recordingWithPlaybackState.inBufferWindow && {
                        display: debugShowBufferingRecordings
                            ? "block"
                            : "none",
                        opacity: "75%",
                        backgroundColor: "orange",
                    }),
                    ...(!recordingWithPlaybackState.inBufferWindow &&
                        !recordingWithPlaybackState.inPlaybackWindow && {
                            display:
                                debugShowRecordingsOutsidePlaybackAndBufferingWindow
                                    ? "block"
                                    : "none",
                            opacity: "35%",
                            backgroundColor: "purple",
                        }),
                }}
                cardActions={
                    <>
                        <Tooltip
                            title={tooltipText}
                            PopperProps={{
                                container: playerCardRef.current?.root,
                            }}
                        >
                            <AccessTimeIcon sx={{ marginLeft: 2 }} />
                        </Tooltip>
                        <Box
                            sx={{
                                flexGrow: 1,
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {recordingWithPlaybackState.recordingId}
                            </Typography>
                        </Box>

                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.PlaybackSingleDownload,
                            ]}
                        >
                            <DownloadRecordingButton
                                recordingId={props.recordingId}
                                deviceId={props.deviceId}
                                startTime={recordingWithPlaybackState.startTime}
                                stopTime={recordingWithPlaybackState.stopTime}
                            />
                        </FeatureFlagGuard>
                    </>
                }
            >
                {!recordingFailedToLoad && isLoading && (
                    <PlayerCardStatus mode={"progress"} />
                )}

                {recordingFailedToLoad && (
                    <PlayerCardStatus
                        mode={"status"}
                        statusIcon={<VideocamOffIcon sx={{ fontSize: 150 }} />}
                    />
                )}

                {!isLoading && !recordingFailedToLoad && (
                    <HlsPlayer
                        ref={playerRef}
                        url={streamUrl}
                        playMode={playMode}
                        playSpeed={1}
                        enableAudio={false}
                        audioEnabledByDefault={audioEnabledByDefault}
                        onCanPlayThrough={onRecordingReadyForPlayback}
                        onClickVideo={() => {
                            if (!document.fullscreenEnabled) return;

                            if (playerCardRef.current?.root) {
                                if (!document.fullscreenElement) {
                                    playerCardRef.current.root.requestFullscreen(
                                        {
                                            navigationUI: "show",
                                        },
                                    );
                                } else {
                                    document.exitFullscreen();
                                }
                            }
                        }}
                        videoMouseOverCursor={videoMouseOverCursor}
                        onStreamQualityErrorCountChange={() => {
                            // ignore event
                        }}
                        playerMode={StreamPlayerModeType.Playback}
                    />
                )}
            </PlayerCard>
        );
    }, [
        debugShowBufferingRecordings,
        debugShowRecordingsOutsidePlaybackAndBufferingWindow,
        inWindowMode,
        isLoading,
        onRecordingReadyForPlayback,
        onStreamWaitingForData,
        playMode,
        props.deviceId,
        props.recordingId,
        recordingFailedToLoad,
        recordingWithPlaybackState,
        shouldBeBufferingOrPlaying,
        streamUrl,
    ]);

    return (
        <>
            {(shouldBeBufferingOrPlaying ||
                debugShowRecordingsOutsidePlaybackAndBufferingWindow) && (
                <Box
                    sx={{
                        display: show ? "block" : "none",
                    }}
                >
                    {cachedHlsPlayerCard}
                </Box>
            )}
        </>
    );
}
